import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, styled, Typography } from '@mui/material';

const LANGUAGES = [
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
] as const;

interface LanguageSwitcherProps {
  /** 아이콘만 표시 (헤더용) */
  iconOnly?: boolean;
}

/**
 * 다국어 전환 버튼
 */
export function LanguageSwitcher({ iconOnly = true }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // 현재 언어 코드 (ko, en, de 등)
  const currentLangCode = i18n.language?.split('-')[0] || 'ko';
  const currentLanguage = LANGUAGES.find((lang) => lang.code === currentLangCode) || LANGUAGES[0];

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = async (langCode: string) => {
    try {
      await i18n.changeLanguage(langCode);
      // console.log('🌐 Language changed to:', i18n.language);
      // console.log('🌐 Available languages:', Object.keys(i18n.options.resources || {}));
    } catch (error) {
      console.error('🌐 Language change error:', error);
    }
    handleClose();
  };

  if (iconOnly) {
    return (
      <>
        <StyledIconButton onClick={handleOpen} data-testid="language-switcher">
          <LangCode>{currentLangCode.toUpperCase()}</LangCode>
          <ExpandIcon>▾</ExpandIcon>
        </StyledIconButton>

        <StyledMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {LANGUAGES.map((lang) => (
            <StyledMenuItem
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              selected={lang.code === currentLangCode}
            >
              <Flag>{lang.flag}</Flag>
              <LangLabel>{lang.label}</LangLabel>
              {lang.code === currentLangCode && <CheckMark>✓</CheckMark>}
            </StyledMenuItem>
          ))}
        </StyledMenu>
      </>
    );
  }

  // 설정 페이지용 (텍스트 포함)
  return (
    <>
      <LanguageButton onClick={handleOpen} data-testid="language-button">
        <Flag>{currentLanguage.flag}</Flag>
        <LangLabel>{currentLanguage.label}</LangLabel>
        <Arrow>▾</Arrow>
      </LanguageButton>

      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {LANGUAGES.map((lang) => (
          <StyledMenuItem
            key={lang.code}
            onClick={() => handleSelect(lang.code)}
            selected={lang.code === currentLangCode}
          >
            <Flag>{lang.flag}</Flag>
            <LangLabel>{lang.label}</LangLabel>
            {lang.code === currentLangCode && <CheckMark>✓</CheckMark>}
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </>
  );
}

// --- Styled Components ---

const StyledIconButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  minWidth: '48px',
  height: '32px',
  padding: '0 8px',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  cursor: 'pointer',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: theme.palette.action.hover,
    borderColor: theme.palette.primary.light,
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

const LangCode = styled('span')(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  letterSpacing: '0.02em',
}));

const ExpandIcon = styled('span')(({ theme }) => ({
  fontSize: '10px',
  color: theme.palette.text.secondary,
  marginTop: '1px',
}));

const LanguageButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 16px',
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: theme.palette.action.hover,
    borderColor: theme.palette.primary.light,
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: '8px',
    minWidth: '160px',
    borderRadius: '12px',
    boxShadow: theme.customShadows.dropdown,
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  fontSize: '14px',
  '&.Mui-selected': {
    background: theme.palette.primary.lighter,
    color: theme.palette.primary.main,
    fontWeight: 600,
    '&:hover': {
      background: theme.palette.primary.light,
    },
  },
}));

const Flag = styled('span')(() => ({
  fontSize: '20px',
  lineHeight: 1,
}));

const LangLabel = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: 1.4,
}));

const Arrow = styled('span')(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.secondary,
  marginLeft: 'auto',
}));

const CheckMark = styled('span')(({ theme }) => ({
  marginLeft: 'auto',
  fontSize: '16px',
  fontWeight: 700,
  color: theme.palette.primary.main,
}));
